import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLines, EASE } from "./Motion";
import Mountains from "./Mountains";
import Snowfall from "./Snowfall";
import { IMG } from "../../data/content";
import { scrollToId } from "../../lib/smooth";

export default function Hero({ ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yGiant = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const rotY = useTransform(sx, [-1, 1], [-8, 8]);
  const rotX = useTransform(sy, [-1, 1], [6, -6]);
  const onMove = (e) => {
    mx.set((e.clientX / window.innerWidth) * 2 - 1);
    my.set((e.clientY / window.innerHeight) * 2 - 1);
  };
  const appear = (d) => ({ initial: { opacity: 0, y: 24 }, animate: ready ? { opacity: 1, y: 0 } : {}, transition: { duration: 1, delay: d, ease: EASE } });

  return (
    <section id="top" ref={ref} data-orbit onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-mist" data-testid="hero-section">
      <motion.div style={{ y: yPhoto }} className="absolute inset-0">
        <img src={IMG("himalaya.jpg")} alt="Snow-capped Himalayan peaks" className="h-full w-full object-cover opacity-30 grayscale mix-blend-multiply" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(199,230,234,.9)_0%,rgba(199,230,234,.35)_45%,#C7E6EA_95%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(45%_55%_at_72%_50%,rgba(255,255,255,.7),transparent_70%)]" />
      </motion.div>

      <div style={{ perspective: 1200 }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <motion.div style={{ y: yGiant, rotateX: rotX, rotateY: rotY }} className="absolute -right-[6vw] top-[14vh] select-none font-deva text-[62vw] leading-[0.8] text-forest/[0.08] lg:top-[6vh] lg:text-[38vw]">
          हो!
        </motion.div>
      </div>

      <Mountains progress={scrollYProgress} />
      <Snowfall />

      <motion.div style={{ opacity: fade }} className="wrap relative z-10 grid min-h-[100svh] grid-cols-1 items-center gap-6 pb-16 pt-28 lg:grid-cols-12 lg:pb-10">
        <div className="lg:col-span-7">
          <motion.p {...appear(0.1)} className="eyebrow flex items-center gap-3 text-forest">
            <span className="h-px w-10 bg-forest" /> Est. 2012 · Yorkshire ⇄ Himachal
          </motion.p>
          <h1 className="mt-7 font-display font-semibold leading-[0.92] tracking-tight text-forest-deep" data-testid="hero-headline">
            <MaskLines
              play={ready}
              delay={0.2}
              lines={[
                <span key="a" className="block py-2"><img src="/assets/logo.webp" alt="HIMACHAL हो!" data-testid="hero-logo" className="h-auto w-[340px] max-w-full sm:w-[460px] lg:w-[600px] xl:w-[660px]" /></span>,
                <span key="b" className="text-4xl italic text-forest-deep/90 sm:text-5xl lg:text-6xl">Britain's Favourite</span>,
                <span key="c" className="text-4xl sm:text-5xl lg:text-6xl">Indian Cider</span>,
              ]}
            />
          </h1>
          <motion.p {...appear(0.9)} className="mt-8 max-w-xl text-base leading-relaxed text-forest-deep/90 md:text-lg" data-testid="hero-tagline">
            Where Every Sip Tells a Story <span className="mx-2 text-forest">|</span> Naturally Crafted in the Himalayas
          </motion.p>
          <motion.div {...appear(1.05)} className="mt-10 flex flex-wrap gap-3">
            <button data-testid="hero-explore-btn" onClick={() => scrollToId("craft")} className="btn-gold">
              Explore the Journey <ArrowDown className="h-4 w-4" />
            </button>
            <button data-testid="hero-ciders-btn" onClick={() => scrollToId("ciders")} className="btn-ghost-dark">
              Discover the Ciders <ArrowUpRight className="h-4 w-4" />
            </button>
          </motion.div>
          <motion.div {...appear(1.2)} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[12px] uppercase tracking-[0.2em] text-forest-deep/85">
            <span>330ml can</span><span>&lt;5% ABV</span><span>100% Himachali apples</span>
          </motion.div>
        </div>
        <div className="relative h-[56svh] lg:col-span-5 lg:h-[80vh]" data-can-stage data-rot="0" data-variant="original" data-testid="hero-can-stage">
          <motion.span {...appear(1.4)} className="absolute bottom-2 left-0 font-mono lg:left-auto lg:right-24 text-[11px] uppercase tracking-[0.22em] text-white">Drag to rotate ⟲</motion.span>
        </div>
      </motion.div>

      <motion.button {...appear(1.5)} onClick={() => scrollToId("craft")} data-testid="hero-scroll-cue" className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white lg:flex">
        Scroll
        <span className="relative h-10 w-px overflow-hidden bg-white/30">
          <motion.span className="absolute left-0 top-0 h-4 w-px bg-gold" animate={{ y: [-16, 40] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
        </span>
      </motion.button>
    </section>
  );
}
